/**
 * Vuex Module: E-Invoice
 *
 * Manages state for the E-Invoice module including
 * invoice list, filters, detail, and correction flows.
 *
 * Refactored to use real API endpoints.
 */
import ApiService from '@/core/services/api.service'
import moment from 'moment'
import { EINVOICE_SALE_TYPE, EINVOICE_STATUS } from '@/utils/einvoice.enum'
import {
  adaptDeleteDraftResponse,
  buildBulkIssueSubmitFingerprint,
  normalizeBulkIssueProgressResponse,
  normalizeBulkIssueSubmitRequest,
  normalizeBulkIssueSubmitResponse,
  normalizeBulkPrecheckRequest,
  normalizeBulkPrecheckResponse,
  normalizeArtifacts,
  normalizeCanonicalReadModel,
  normalizeIssuedPdfPayload,
  normalizeInvoiceDetail,
  normalizeInvoiceListResponse,
  normalizePreviewPdfPayload,
  normalizeReadiness,
  normalizeReadinessSummary,
  normalizeRetailFacet,
  normalizeStatus,
  normalizeTaxStatus,
} from './einvoice.adapter'
import {
  EINVOICE_BASE_URL as BASE_URL,
  DEFAULT_EINVOICE_LIST_BILL_TYPES,
  buildLaneKey,
  buildBatchFacetEndpoint,
  buildCanonicalDetailEndpoint,
  buildDeleteDraftEndpoint,
  buildDraftPrefix,
  inferPdfEndpoint,
  isRetailLikeBillType,
  normalizeBillTypesOrDefault,
  normalizeSaleType,
  requireBillTypes,
  requireCanonicalSaleType,
  serializeEInvoiceListParams,
} from './einvoice.contract'
import { requireCanonicalActionContext } from './einvoice.action-context'
import {
  buildCancelPayload,
  buildResendEmailPayload,
  normalizeCanonicalAdjustPayload,
  normalizeCanonicalReplacePayload,
} from './einvoice.action-payload'
import {
  createBulkIssuePrecheckError,
  createBulkIssueProgressError,
  createBulkIssueSubmitError,
  createClientValidationError,
  createDeleteDraftError,
  createEInvoiceActionError,
} from './einvoice.action-errors'
import {
  adjustEInvoiceAction,
  cancelEInvoiceAction,
  issueEInvoiceAction,
  refreshEInvoiceTaxStatusAction,
  replaceEInvoiceAction,
  resendEInvoiceEmailAction,
} from './einvoice.action-api'

// ─── Action types ──────────────────────────────────────────
export const EINVOICE_FETCH_LIST = 'einvoice/fetchList'
export const EINVOICE_FETCH_FACET = 'einvoice/fetchFacet'
export const EINVOICE_FETCH_FACETS_BATCH = 'einvoice/fetchFacetsBatch'
export const EINVOICE_FETCH_DETAIL = 'einvoice/fetchDetail'
export const EINVOICE_ISSUE = 'einvoice/issue'
export const EINVOICE_CANCEL = 'einvoice/cancel'
export const EINVOICE_ADJUST = 'einvoice/adjust'
export const EINVOICE_REPLACE = 'einvoice/replace'
export const EINVOICE_REFRESH_TAX = 'einvoice/refreshTaxStatus'
export const EINVOICE_FETCH_PDF = 'einvoice/fetchPdf'
export const EINVOICE_RESEND_EMAIL = 'einvoice/resendEmail'
export const EINVOICE_CREATE_DRAFT = 'einvoice/createDraft'
export const EINVOICE_UPDATE_DRAFT = 'einvoice/updateDraft'
export const EINVOICE_VALIDATE_DRAFT = 'einvoice/validateDraft'
export const EINVOICE_SET_FILTER = 'einvoice/setFilter'
export const EINVOICE_RESET_FILTER = 'einvoice/resetFilter'
export const EINVOICE_CREATE_DRAFT_BATCH = 'einvoice/createDraftBatch'
export const EINVOICE_POLL_JOB = 'einvoice/pollJob'
export const EINVOICE_LOOKUP_TAX_CODE = 'einvoice/lookupTaxCode'
export const EINVOICE_PREVIEW_CALCULATION = 'einvoice/previewCalculation'
export const EINVOICE_CLEAR_PREVIEW = 'einvoice/clearPreview'
export const EINVOICE_FETCH_BILL_INFO = 'einvoice/fetchBillInfo'
export const EINVOICE_BULK_ISSUE_PRECHECK = 'einvoice/bulkIssuePrecheck'
export const EINVOICE_BULK_ISSUE_SUBMIT = 'einvoice/bulkIssueSubmit'
export const EINVOICE_BULK_ISSUE_PROGRESS = 'einvoice/bulkIssueProgress'
export const EINVOICE_DELETE_DRAFT = 'einvoice/deleteDraft'
export const EINVOICE_BULK_DELETE_DRAFT = 'einvoice/bulkDeleteDraft'

// ─── Mutation types ────────────────────────────────────────
const SET_LIST = 'SET_LIST'
const SET_FACET = 'SET_FACET'
const SET_FACET_LOADING = 'SET_FACET_LOADING'
const SET_DETAIL_CURRENT_KEY = 'SET_DETAIL_CURRENT_KEY'
const SET_DETAIL = 'SET_DETAIL'
const SET_LOADING = 'SET_LOADING'
const SET_DETAIL_LOADING = 'SET_DETAIL_LOADING'
const SET_DETAIL_ERROR = 'SET_DETAIL_ERROR'
const SET_ACTION_LOADING = 'SET_ACTION_LOADING'
const SET_ERROR = 'SET_ERROR'
const SET_FILTER = 'SET_FILTER'
const SET_PAGINATION = 'SET_PAGINATION'
const RESET_FILTER = 'RESET_FILTER'
const SET_BATCH_LOADING = 'SET_BATCH_LOADING'
const SET_VAT_PREVIEW_LOADING = 'SET_VAT_PREVIEW_LOADING'
const SET_VAT_PREVIEW_DATA = 'SET_VAT_PREVIEW_DATA'
const SET_VAT_PREVIEW_ERROR = 'SET_VAT_PREVIEW_ERROR'
const CLEAR_VAT_PREVIEW = 'CLEAR_VAT_PREVIEW'
const SET_BILL_INFO_LOADING = 'SET_BILL_INFO_LOADING'
const SET_BILL_INFO_DATA = 'SET_BILL_INFO_DATA'
const SET_BILL_INFO_ERROR = 'SET_BILL_INFO_ERROR'
const CLEAR_BILL_INFO = 'CLEAR_BILL_INFO'
const SET_BULK_ISSUE_PRE_CHECKING = 'SET_BULK_ISSUE_PRE_CHECKING'
const SET_BULK_ISSUE_PRE_CHECK_RESULT = 'SET_BULK_ISSUE_PRE_CHECK_RESULT'
const SET_BULK_ISSUE_PROCESSING = 'SET_BULK_ISSUE_PROCESSING'
const SET_BULK_ISSUE_JOB_ID = 'SET_BULK_ISSUE_JOB_ID'
const SET_BULK_ISSUE_PROGRESS = 'SET_BULK_ISSUE_PROGRESS'
const SET_BULK_ISSUE_RESULT = 'SET_BULK_ISSUE_RESULT'
const SET_BULK_ISSUE_ERROR = 'SET_BULK_ISSUE_ERROR'
const SET_BULK_ISSUE_PHASE = 'SET_BULK_ISSUE_PHASE'
const SET_BULK_ISSUE_REQUEST_PAYLOAD = 'SET_BULK_ISSUE_REQUEST_PAYLOAD'
const SET_BULK_ISSUE_IDEMPOTENCY = 'SET_BULK_ISSUE_IDEMPOTENCY'
const RESET_BULK_ISSUE = 'RESET_BULK_ISSUE'
const REMOVE_LIST_ITEM = 'REMOVE_LIST_ITEM'

// ─── Bulk Delete mutation types (module-private) ───────────
const SET_BULK_DELETE_PHASE = 'SET_BULK_DELETE_PHASE'
const SET_BULK_DELETE_ELIGIBLE = 'SET_BULK_DELETE_ELIGIBLE'
const SET_BULK_DELETE_PROGRESS = 'SET_BULK_DELETE_PROGRESS'
const SET_BULK_DELETE_RESULT = 'SET_BULK_DELETE_RESULT'
const SET_BULK_DELETE_ERROR = 'SET_BULK_DELETE_ERROR'
const RESET_BULK_DELETE = 'RESET_BULK_DELETE'

// ─── Default filter ────────────────────────────────────────
const defaultFilter = () => ({
  billTypes: [...DEFAULT_EINVOICE_LIST_BILL_TYPES],
  status: null,
  keyword: '',
  buyerTaxCode: '',
  codeShip: '',
  dateFrom: null,
  dateTo: null,
  issueDateFrom: null,
  issueDateTo: null,
  storeIds: [],
  page: 1,
  limit: 20,
})

function extractResponseData(payload) {
  return payload?.data || payload || {}
}

function firstDefined(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '')
}

function normalizePreviewItems(source = []) {
  if (Array.isArray(source)) {
    return normalizeCanonicalReadModel({
      items: source,
      strictItemContract: true,
      itemCollectionPath: 'items',
    }).items
  }

  return normalizeCanonicalReadModel({
    source,
    strictItemContract: true,
  }).items
}

function normalizeFilterKeyword(keyword) {
  return typeof keyword === 'string' ? keyword.trim() : ''
}

function normalizeRouteKeyValue(value) {
  if (value === null || value === undefined) {
    return null
  }

  const normalized = String(value).trim()
  return normalized || null
}

function buildSalesInvoiceLaneKey(salesInvoiceId, saleType = null) {
  return buildLaneKey(salesInvoiceId, saleType)
}

function createDetailCacheState() {
  return {
    currentKey: null,
    entitiesByKey: {},
    loadingByKey: {},
    errorByKey: {},
  }
}

function getCurrentDetailEntity(state) {
  const currentKey = state?.detail?.currentKey
  if (!currentKey) {
    return null
  }

  return state?.detail?.entitiesByKey?.[currentKey] || null
}

function getDetailEntityByKey(state, key) {
  if (!key) {
    return null
  }

  return state?.detail?.entitiesByKey?.[key] || null
}

function findCachedFacetEntry(cache = {}, salesInvoiceId, saleType = null) {
  const normalizedId = normalizeRouteKeyValue(salesInvoiceId)
  if (!normalizedId) {
    return { key: null, value: null }
  }

  const normalizedSaleType = normalizeSaleType(saleType)
  if (normalizedSaleType) {
    const laneKey = buildSalesInvoiceLaneKey(normalizedId, normalizedSaleType)
    return {
      key: laneKey,
      value: Object.prototype.hasOwnProperty.call(cache, laneKey) ? cache[laneKey] : null,
    }
  }

  if (Object.prototype.hasOwnProperty.call(cache, normalizedId)) {
    return {
      key: normalizedId,
      value: cache[normalizedId],
    }
  }

  const fallbackValue =
    Object.values(cache).find(
      (facet) => normalizeRouteKeyValue(facet?.salesInvoiceId) === normalizedId,
    ) || null

  return {
    key: normalizedId,
    value: fallbackValue,
  }
}

function findFacetLoadingState(cache = {}, salesInvoiceId, saleType = null) {
  const normalizedId = normalizeRouteKeyValue(salesInvoiceId)
  if (!normalizedId) {
    return false
  }

  const normalizedSaleType = normalizeSaleType(saleType)
  if (normalizedSaleType) {
    const laneKey = buildSalesInvoiceLaneKey(normalizedId, normalizedSaleType)
    return Boolean(cache[laneKey])
  }

  if (Object.prototype.hasOwnProperty.call(cache, normalizedId)) {
    return Boolean(cache[normalizedId])
  }

  return Object.entries(cache).some(
    ([key, value]) => key.endsWith(`::${normalizedId}`) && Boolean(value),
  )
}

export function normalizeFilterDate(value) {
  if (!value) return null

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : moment(value).format('YYYY-MM-DD')
  }

  if (typeof value === 'string') {
    const parsed = moment(value, ['YYYY-MM-DD', moment.ISO_8601], true)
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null
  }

  return null
}

function normalizeStoreIds(value) {
  if (!Array.isArray(value)) {
    return []
  }
  return value.filter((id) => id !== null && id !== undefined && id !== '')
}

export function normalizeAppliedFilter(filter = {}, baseFilter = defaultFilter()) {
  const billTypes = Object.prototype.hasOwnProperty.call(filter, 'billTypes')
    ? normalizeBillTypesOrDefault(filter.billTypes)
    : normalizeBillTypesOrDefault(baseFilter.billTypes)

  return {
    billTypes,
    status: filter.status || null,
    keyword: normalizeFilterKeyword(
      Object.prototype.hasOwnProperty.call(filter, 'keyword') ? filter.keyword : baseFilter.keyword,
    ),
    buyerTaxCode: normalizeFilterKeyword(
      Object.prototype.hasOwnProperty.call(filter, 'buyerTaxCode')
        ? filter.buyerTaxCode
        : baseFilter.buyerTaxCode,
    ),
    codeShip: normalizeFilterKeyword(
      Object.prototype.hasOwnProperty.call(filter, 'codeShip')
        ? filter.codeShip
        : baseFilter.codeShip,
    ),
    dateFrom: normalizeFilterDate(
      Object.prototype.hasOwnProperty.call(filter, 'dateFrom') ? filter.dateFrom : baseFilter.dateFrom,
    ),
    dateTo: normalizeFilterDate(
      Object.prototype.hasOwnProperty.call(filter, 'dateTo') ? filter.dateTo : baseFilter.dateTo,
    ),
    issueDateFrom: normalizeFilterDate(
      Object.prototype.hasOwnProperty.call(filter, 'issueDateFrom')
        ? filter.issueDateFrom
        : baseFilter.issueDateFrom,
    ),
    issueDateTo: normalizeFilterDate(
      Object.prototype.hasOwnProperty.call(filter, 'issueDateTo')
        ? filter.issueDateTo
        : baseFilter.issueDateTo,
    ),
    storeIds: normalizeStoreIds(filter.storeIds),
    page: Number.isInteger(filter.page) && filter.page > 0 ? filter.page : baseFilter.page,
    limit: Number.isInteger(filter.limit) && filter.limit > 0 ? filter.limit : baseFilter.limit,
  }
}

export function hasFilterCriteriaChanged(prevFilter = {}, nextFilter = {}) {
  const criteriaKeys = [
    'status',
    'keyword',
    'buyerTaxCode',
    'codeShip',
    'dateFrom',
    'dateTo',
    'issueDateFrom',
    'issueDateTo',
  ]

  const scalarChanged = criteriaKeys.some((key) => {
    const prevValue = firstDefined(prevFilter[key], null)
    const nextValue = firstDefined(nextFilter[key], null)
    return prevValue !== nextValue
  })

  const billTypesChanged =
    normalizeBillTypesOrDefault(prevFilter.billTypes).join('|') !==
    normalizeBillTypesOrDefault(nextFilter.billTypes).join('|')

  const storeIdsChanged =
    normalizeStoreIds(prevFilter.storeIds).sort().join('|') !==
    normalizeStoreIds(nextFilter.storeIds).sort().join('|')

  return scalarChanged || billTypesChanged || storeIdsChanged
}

export function resetSubordinateFilters(filter = {}) {
  const currentFilter = normalizeAppliedFilter({
    ...filter,
    billTypes: [...DEFAULT_EINVOICE_LIST_BILL_TYPES],
  })

  return {
    ...currentFilter,
    billTypes: [...DEFAULT_EINVOICE_LIST_BILL_TYPES],
    status: null,
    keyword: '',
    buyerTaxCode: '',
    dateFrom: null,
    dateTo: null,
    issueDateFrom: null,
    issueDateTo: null,
    storeIds: [],
    page: 1,
  }
}

function toNumber(value, fallback = 0) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function normalizeStringValue(value) {
  if (typeof value !== 'string') {
    return value === null || value === undefined ? null : String(value).trim() || null
  }

  const normalized = value.trim()
  return normalized || null
}

function normalizeErrorCode(value) {
  if (typeof value !== 'string') {
    return value === undefined ? null : value
  }

  const normalized = value.trim().toUpperCase()
  return normalized || null
}

const PREVIEW_BUSINESS_ERROR_MESSAGES = Object.freeze({
  SOURCE_ITEMS_CONTRACT_INVALID: 'Dữ liệu nguồn hóa đơn không đúng contract để dựng preview VAT.',
  SOURCE_ITEM_VAT_MISSING:
    'Thiếu VAT ở một số dòng hàng nguồn đang hoạt động. Vui lòng kiểm tra Product.VAT.',
  SOURCE_ITEM_VAT_INVALID:
    'VAT của hàng hóa nguồn không hợp lệ. Preview chỉ hỗ trợ 0, 5, 8, 10, -1 (KCT). Vui lòng kiểm tra Product.VAT.',
  UNSUPPORTED_VAT_RATE:
    'Cấu hình VAT không hợp lệ. Preview chỉ hỗ trợ 0, 5, 8, 10, -1 (KCT). Vui lòng kiểm tra cấu hình E-Invoice.',
  DISCOUNT_BUCKET_ALLOCATION_FAILED:
    'Không thể phân bổ chiết khấu tổng hợp theo bucket thuế hiện tại.',
  BILL_TYPE_MISMATCH: 'Hóa đơn không thuộc lane e-invoice đã chọn.',
  WHOLESALE_INVOICE_NOT_FOUND: 'Không tìm thấy hóa đơn bán buôn để dựng preview.',
})

const PREVIEW_CANONICAL_TAX_BREAKDOWN_TEMPLATE = Object.freeze([
  Object.freeze({ taxRate: 0 }),
  Object.freeze({ taxRate: 5 }),
  Object.freeze({ taxRate: 8 }),
  Object.freeze({ taxRate: 10 }),
  Object.freeze({ taxRate: 0, taxCode: 'KCT' }),
])

function buildPreviewTaxBucketKey({ taxRate = 0, taxCode = null } = {}) {
  return `${toNumber(taxRate, 0)}::${normalizePreviewTaxCode(taxCode) || ''}`
}

function createPreviewCanonicalTaxBreakdownState() {
  return PREVIEW_CANONICAL_TAX_BREAKDOWN_TEMPLATE.reduce((accumulator, bucket) => {
    const bucketKey = buildPreviewTaxBucketKey(bucket)
    accumulator[bucketKey] = {
      taxRate: bucket.taxRate,
      ...(bucket.taxCode ? { taxCode: bucket.taxCode } : {}),
      totalBeforeTax: 0,
      totalVat: 0,
    }
    return accumulator
  }, {})
}

function assignIfPresent(target, key, value) {
  const normalized = normalizeStringValue(value)
  if (normalized !== null) {
    target[key] = normalized
  }
}

function extractFirstResult(payload) {
  const source = extractResponseData(payload)

  if (Array.isArray(source?.results)) {
    return source.results[0] || null
  }

  if (Array.isArray(source?.items)) {
    return source.items[0] || null
  }

  if (Array.isArray(source)) {
    return source[0] || null
  }

  return source?.result || source || null
}

function resolveDetailRequestContext(state, payload) {
  const rawPayload = payload && typeof payload === 'object' ? payload : {}
  const salesInvoiceId = normalizeRouteKeyValue(firstDefined(rawPayload.salesInvoiceId, null))
  const saleType = normalizeSaleType(firstDefined(rawPayload.saleType, null))

  if (!salesInvoiceId) {
    const error = new Error('Thiếu salesInvoiceId để tải chi tiết HĐĐT')
    error.code = 'EINVOICE_SALES_INVOICE_ID_REQUIRED'
    throw error
  }

  if (!saleType) {
    const error = new Error(
      `Thiếu saleType hợp lệ để tải chi tiết HĐĐT cho salesInvoiceId=${String(salesInvoiceId)}`,
    )
    error.code = 'EINVOICE_SALE_TYPE_REQUIRED'
    throw error
  }

  return {
    salesInvoiceId,
    saleType,
    laneKey: buildSalesInvoiceLaneKey(salesInvoiceId, saleType),
  }
}

const CANONICAL_DRAFT_OVERRIDE_KEYS = Object.freeze([
  'buyerType',
  'customerName',
  'contactPersonName',
  'customerTaxCode',
  'customerAddress',
  'customerEmail',
  'customerPhone',
  'customerIdNumber',
  'paymentMethod',
  'note',
  'currency',
  'exchangeRate',
])

const CANONICAL_BUYER_TYPES = new Set(['ENTERPRISE', 'INDIVIDUAL'])
const CANONICAL_DRAFT_OVERRIDE_KEY_SET = new Set(CANONICAL_DRAFT_OVERRIDE_KEYS)
const DRAFT_BATCH_ALLOWED_TOP_LEVEL_KEYS = new Set(['billIds', 'overrides'])
const PREVIEW_CALCULATION_ALLOWED_TOP_LEVEL_KEYS = new Set(['saleType', 'invoices'])
const PREVIEW_CALCULATION_ALLOWED_INVOICE_KEYS = new Set(['salesInvoiceId', 'override'])

function normalizeCanonicalNullableValue(value) {
  if (value === null || value === undefined) {
    return null
  }

  if (typeof value === 'string') {
    const normalized = value.trim()
    return normalized || null
  }

  const normalized = String(value).trim()
  return normalized || null
}

function normalizeCanonicalExchangeRate(value) {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function normalizeCanonicalBuyerType(value) {
  const raw = normalizeCanonicalNullableValue(value)
  if (!raw) {
    return null
  }

  const normalized = raw.toUpperCase()
  return CANONICAL_BUYER_TYPES.has(normalized) ? normalized : null
}

function buildCanonicalDraftOverride(payload = {}, { path = 'override' } = {}) {
  const source = payload && typeof payload === 'object' ? payload : {}
  const extraKeys = Object.keys(source).filter((key) => !CANONICAL_DRAFT_OVERRIDE_KEY_SET.has(key))
  const missingKeys = CANONICAL_DRAFT_OVERRIDE_KEYS.filter(
    (key) => !Object.prototype.hasOwnProperty.call(source, key),
  )

  if (extraKeys.length || missingKeys.length) {
    const fragments = [`${path} phải dùng đúng canonical override shape 12 keys.`]

    if (missingKeys.length) {
      fragments.push(`Thiếu: ${missingKeys.join(', ')}.`)
    }

    if (extraKeys.length) {
      fragments.push(`Thừa: ${extraKeys.join(', ')}.`)
    }

    throw createClientValidationError('DRAFT_STRICT_SCHEMA_INVALID', fragments.join(' '), {
      path,
      missingKeys,
      extraKeys,
    })
  }

  const buyerType = normalizeCanonicalBuyerType(source.buyerType)
  if (!buyerType) {
    throw createClientValidationError(
      'DRAFT_STRICT_SCHEMA_INVALID',
      `${path}.buyerType phải là ENTERPRISE hoặc INDIVIDUAL.`,
      {
        path,
        field: 'buyerType',
        receivedValue: source.buyerType,
      },
    )
  }

  const override = {
    buyerType,
    customerName: normalizeCanonicalNullableValue(source.customerName),
    contactPersonName: normalizeCanonicalNullableValue(source.contactPersonName),
    customerTaxCode: normalizeCanonicalNullableValue(source.customerTaxCode),
    customerAddress: normalizeCanonicalNullableValue(source.customerAddress),
    customerEmail: normalizeCanonicalNullableValue(source.customerEmail),
    customerPhone: normalizeCanonicalNullableValue(source.customerPhone),
    customerIdNumber: normalizeCanonicalNullableValue(source.customerIdNumber),
    paymentMethod: normalizeCanonicalNullableValue(source.paymentMethod),
    note: normalizeCanonicalNullableValue(source.note),
    currency: normalizeCanonicalNullableValue(source.currency) || 'VND',
    exchangeRate: normalizeCanonicalExchangeRate(source.exchangeRate),
  }

  // Keep a strict key boundary for backend schema validation.
  const normalizedOverride = {}
  CANONICAL_DRAFT_OVERRIDE_KEYS.forEach((key) => {
    normalizedOverride[key] = override[key]
  })

  return normalizedOverride
}

function normalizeDraftBatchPayload(payload = {}) {
  const source = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {}
  const invalidTopLevelKeys = Object.keys(source).filter(
    (key) => !DRAFT_BATCH_ALLOWED_TOP_LEVEL_KEYS.has(key),
  )

  if (invalidTopLevelKeys.length) {
    throw createClientValidationError(
      'DRAFT_BATCH_EXTRA_TOP_LEVEL_KEYS',
      `Payload tạo nháp chỉ chấp nhận billIds và overrides. Keys không hợp lệ: ${invalidTopLevelKeys.join(', ')}`,
      { invalidTopLevelKeys },
    )
  }

  const rawBillIds = Array.isArray(source?.billIds) ? source.billIds : []
  const normalizedBillIds = []
  const seenBillIds = new Set()
  const duplicateBillIds = new Set()

  rawBillIds.forEach((billId) => {
    if (billId === null || billId === undefined || billId === '') {
      return
    }

    const dedupeKey = String(billId).trim()
    if (!dedupeKey) {
      return
    }

    if (seenBillIds.has(dedupeKey)) {
      duplicateBillIds.add(dedupeKey)
      return
    }

    seenBillIds.add(dedupeKey)
    normalizedBillIds.push(dedupeKey)
  })

  if (duplicateBillIds.size) {
    throw createClientValidationError(
      'DRAFT_BATCH_DUPLICATE_BILL_IDS',
      `Danh sách billIds có phần tử trùng: ${Array.from(duplicateBillIds).join(', ')}`,
      { duplicateBillIds: Array.from(duplicateBillIds) },
    )
  }

  if (!normalizedBillIds.length) {
    throw createClientValidationError(
      'DRAFT_BATCH_EMPTY_BILL_IDS',
      'Cần ít nhất 1 billId hợp lệ để tạo draft',
    )
  }

  const overrides = {}
  const hasOverridesKey = Object.prototype.hasOwnProperty.call(source, 'overrides')
  const rawOverrides = hasOverridesKey ? source.overrides : {}

  if (
    hasOverridesKey &&
    (rawOverrides === null || typeof rawOverrides !== 'object' || Array.isArray(rawOverrides))
  ) {
    throw createClientValidationError(
      'DRAFT_BATCH_INVALID_OVERRIDES_SHAPE',
      'Trường overrides phải là object map theo billId',
    )
  }

  const invalidOverrideKeys = Object.keys(rawOverrides).filter(
    (billId) => !seenBillIds.has(String(billId).trim()),
  )

  if (invalidOverrideKeys.length) {
    throw createClientValidationError(
      'DRAFT_BATCH_OVERRIDE_OUTSIDE_BILL_IDS',
      `overrides chứa billId không nằm trong billIds: ${invalidOverrideKeys.join(', ')}`,
      { invalidOverrideKeys },
    )
  }

  Object.keys(rawOverrides).forEach((billId) => {
    const normalizedBillId = String(billId).trim()
    overrides[normalizedBillId] = buildCanonicalDraftOverride(rawOverrides[billId], {
      path: `overrides.${normalizedBillId}`,
    })
  })

  return { billIds: normalizedBillIds, overrides }
}

function normalizePreviewCalculationRequest(payload = {}) {
  const source = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {}
  const invalidTopLevelKeys = Object.keys(source).filter(
    (key) => !PREVIEW_CALCULATION_ALLOWED_TOP_LEVEL_KEYS.has(key),
  )

  if (invalidTopLevelKeys.length) {
    throw createClientValidationError(
      'PREVIEW_CALCULATION_INVALID_PAYLOAD_SHAPE',
      `Payload preview-calculation chỉ chấp nhận saleType và invoices. Keys không hợp lệ: ${invalidTopLevelKeys.join(', ')}`,
      { invalidTopLevelKeys },
    )
  }

  const normalizedSaleType = requireCanonicalSaleType(
    source?.saleType,
    'Thiếu saleType hợp lệ cho preview-calculation',
  )
  const rawInvoices = Array.isArray(source?.invoices) ? source.invoices : null

  if (!rawInvoices) {
    throw createClientValidationError(
      'PREVIEW_CALCULATION_INVALID_PAYLOAD_SHAPE',
      'Payload preview-calculation phải có shape { saleType, invoices: [{ salesInvoiceId, override }] }',
    )
  }

  const requestInvoices = []
  rawInvoices.forEach((invoice, index) => {
    if (!invoice || typeof invoice !== 'object' || Array.isArray(invoice)) {
      throw createClientValidationError(
        'PREVIEW_CALCULATION_INVALID_PAYLOAD_SHAPE',
        `invoices[${index}] phải là object { salesInvoiceId, override }.`,
        { index },
      )
    }

    const invalidInvoiceKeys = Object.keys(invoice).filter(
      (key) => !PREVIEW_CALCULATION_ALLOWED_INVOICE_KEYS.has(key),
    )

    if (invalidInvoiceKeys.length) {
      throw createClientValidationError(
        'PREVIEW_CALCULATION_INVALID_PAYLOAD_SHAPE',
        `invoices[${index}] chỉ chấp nhận salesInvoiceId và override. Keys không hợp lệ: ${invalidInvoiceKeys.join(', ')}`,
        { index, invalidInvoiceKeys },
      )
    }

    const salesInvoiceId = normalizeCanonicalNullableValue(invoice.salesInvoiceId)
    if (!salesInvoiceId) {
      throw createClientValidationError(
        'PREVIEW_CALCULATION_INVALID_PAYLOAD_SHAPE',
        `Thiếu salesInvoiceId tại invoices[${index}].`,
        { index },
      )
    }

    requestInvoices.push({
      salesInvoiceId,
      override: buildCanonicalDraftOverride(invoice.override, {
        path: `invoices[${index}].override`,
      }),
    })
  })

  if (!requestInvoices.length) {
    throw createClientValidationError(
      'PREVIEW_CALCULATION_EMPTY_INVOICES',
      'Cần ít nhất 1 invoice hợp lệ để xem trước VAT',
    )
  }

  return {
    saleType: normalizedSaleType,
    invoices: requestInvoices,
  }
}

function normalizePreviewBillStatus(bill = {}) {
  const rawStatus = typeof bill?.status === 'string' ? bill.status.trim().toLowerCase() : ''

  if (rawStatus === 'success') {
    return {
      status: 'success',
      success: true,
    }
  }

  if (rawStatus === 'error') {
    return {
      status: 'error',
      success: false,
    }
  }

  const hasErrorSignal = Boolean(
    normalizeStringValue(
      firstDefined(bill?.error, bill?.errorCode, bill?.code, bill?.reasonCode, null),
    ),
  )
  const success = bill?.success !== false && !hasErrorSignal
  return {
    status: success ? 'success' : 'error',
    success,
  }
}

function normalizePreviewBillInvoiceType(rawInvoiceType) {
  const normalizedSaleType = normalizeSaleType(rawInvoiceType)

  if (normalizedSaleType === EINVOICE_SALE_TYPE.RETAIL) {
    return 'retail'
  }

  if (normalizedSaleType === EINVOICE_SALE_TYPE.WHOLESALE) {
    return 'wholesale'
  }

  return null
}

function normalizePreviewTaxCode(value) {
  const normalized = normalizeStringValue(value)
  return normalized ? String(normalized).toUpperCase() : null
}

function normalizePreviewTaxBucket(taxRate, taxCode = null) {
  const normalizedRate = toNumber(taxRate, Number.NaN)
  const normalizedTaxCode = normalizePreviewTaxCode(taxCode)
  const isKctBucket = normalizedTaxCode === 'KCT' || normalizedRate === -1

  if (isKctBucket) {
    return {
      taxRate: 0,
      taxCode: 'KCT',
    }
  }

  if (!Number.isFinite(normalizedRate) || ![0, 5, 8, 10].includes(normalizedRate)) {
    return null
  }

  if (normalizedTaxCode) {
    return null
  }

  return {
    taxRate: normalizedRate,
    taxCode: null,
  }
}

function normalizePreviewTaxBreakdownEntry(entry = {}) {
  const rawTaxRate = firstDefined(entry?.taxRate, entry?.vatRate, 0)
  const rawTaxCode = firstDefined(entry?.taxCode, entry?.vatCode, null)
  const taxCode = normalizePreviewTaxCode(rawTaxCode)
  const normalizedBucket = normalizePreviewTaxBucket(rawTaxRate, taxCode)

  return {
    taxRate: normalizedBucket ? normalizedBucket.taxRate : toNumber(rawTaxRate, 0),
    ...(normalizedBucket?.taxCode
      ? { taxCode: normalizedBucket.taxCode }
      : taxCode
        ? { taxCode }
        : {}),
    totalBeforeTax: toNumber(entry?.totalBeforeTax, 0),
    totalVat: toNumber(firstDefined(entry?.totalVat, entry?.taxAmount), 0),
  }
}

function normalizePreviewTaxBreakdownRows(rows = []) {
  const grouped = createPreviewCanonicalTaxBreakdownState()
  let hasUnsupportedBucket = false

  rows.forEach((row) => {
    const normalized = normalizePreviewTaxBreakdownEntry(row)
    const normalizedBucket = normalizePreviewTaxBucket(normalized.taxRate, normalized.taxCode)

    if (!normalizedBucket) {
      hasUnsupportedBucket = true
      return
    }

    const bucketKey = buildPreviewTaxBucketKey(normalizedBucket)
    if (!Object.prototype.hasOwnProperty.call(grouped, bucketKey)) {
      hasUnsupportedBucket = true
      return
    }

    grouped[bucketKey].totalBeforeTax += toNumber(normalized.totalBeforeTax, 0)
    grouped[bucketKey].totalVat += toNumber(normalized.totalVat, 0)
  })

  const normalizedRows = PREVIEW_CANONICAL_TAX_BREAKDOWN_TEMPLATE.map((bucket) => {
    const bucketKey = buildPreviewTaxBucketKey(bucket)
    const normalizedBucket = grouped[bucketKey]

    return {
      taxRate: bucket.taxRate,
      ...(bucket.taxCode ? { taxCode: bucket.taxCode } : {}),
      totalBeforeTax: toNumber(normalizedBucket?.totalBeforeTax, 0),
      totalVat: toNumber(normalizedBucket?.totalVat, 0),
    }
  })

  return {
    rows: normalizedRows,
    hasUnsupportedBucket,
  }
}

function derivePreviewTaxBreakdown(items = []) {
  const rows = items.map((item) => ({
    taxRate: firstDefined(item?.taxRate, item?.vatRate, 0),
    taxCode: firstDefined(item?.taxCode, item?.vatCode, null),
    totalBeforeTax: toNumber(firstDefined(item?.totalBeforeTax, item?.amount), 0),
    totalVat: toNumber(firstDefined(item?.totalVat, item?.taxAmount), 0),
  }))

  return normalizePreviewTaxBreakdownRows(rows)
}

function mergePreviewTaxBreakdown(rows = []) {
  return normalizePreviewTaxBreakdownRows(rows)
}

function normalizePreviewBillSummary(summary, items = []) {
  const source = summary && typeof summary === 'object' ? summary : {}
  const normalizedTaxBreakdown = Array.isArray(source.taxBreakdown)
    ? normalizePreviewTaxBreakdownRows(source.taxBreakdown)
    : derivePreviewTaxBreakdown(items)

  return {
    summary: {
      totalBeforeTax: toNumber(source.totalBeforeTax, 0),
      totalVat: toNumber(source.totalVat, 0),
      totalSyntheticDiscountBeforeTax: toNumber(source.totalSyntheticDiscountBeforeTax, 0),
      totalSyntheticDiscount: toNumber(source.totalSyntheticDiscount, 0),
      totalDiscountBeforeTax: toNumber(source.totalDiscountBeforeTax, 0),
      totalDiscount: toNumber(source.totalDiscount, 0),
      totalAmount: toNumber(source.totalAmount, 0),
      currency: firstDefined(source.currency, 'VND'),
      amountInWords: firstDefined(source.amountInWords, null),
      taxBreakdown: normalizedTaxBreakdown.rows,
    },
    hasUnsupportedTaxBucket: normalizedTaxBreakdown.hasUnsupportedBucket,
  }
}

function normalizePreviewAggregateSummary(summary, normalizedBills = []) {
  const source = summary && typeof summary === 'object' ? summary : {}
  const normalizedTaxBreakdown = Array.isArray(source.taxBreakdown)
    ? normalizePreviewTaxBreakdownRows(source.taxBreakdown)
    : mergePreviewTaxBreakdown(
        normalizedBills.flatMap((bill) =>
          Array.isArray(bill?.summary?.taxBreakdown) ? bill.summary.taxBreakdown : [],
        ),
      )

  return {
    totalBeforeTax: toNumber(source.grandTotalBeforeTax, 0),
    totalVat: toNumber(source.grandTotalVat, 0),
    totalSyntheticDiscountBeforeTax: toNumber(source.grandTotalSyntheticDiscountBeforeTax, 0),
    totalSyntheticDiscount: toNumber(source.grandTotalSyntheticDiscount, 0),
    totalDiscountBeforeTax: toNumber(source.grandTotalDiscountBeforeTax, 0),
    totalDiscount: toNumber(source.grandTotalDiscount, 0),
    totalAmount: toNumber(source.grandTotalAmount, 0),
    currency: firstDefined(source.currency, 'VND'),
    amountInWords: firstDefined(source.amountInWords, null),
    taxBreakdown: normalizedTaxBreakdown.rows,
  }
}

function normalizePreviewBillErrorCode(bill = {}) {
  return normalizeErrorCode(firstDefined(bill?.errorCode, bill?.code, bill?.reasonCode, null))
}

function resolvePreviewBillErrorMessage(errorCode, fallbackMessage) {
  const normalizedCode = normalizeErrorCode(errorCode)
  const normalizedMessage = normalizeStringValue(fallbackMessage)

  if (normalizedCode && PREVIEW_BUSINESS_ERROR_MESSAGES[normalizedCode]) {
    return PREVIEW_BUSINESS_ERROR_MESSAGES[normalizedCode]
  }

  return normalizedMessage || 'Không thể dựng preview VAT cho hóa đơn này.'
}

function normalizePreviewCalculationResponse(payload = {}, requestedInvoices = []) {
  const source = extractResponseData(payload)

  if (!Array.isArray(source?.bills) || !source?.summary) {
    return normalizeDraftPreviewPayload(payload, requestedInvoices[0]?.salesInvoiceId || null)
  }

  const normalizedBills = source.bills.map((bill, index) => {
    const resolvedSalesInvoiceId = firstDefined(
      bill?.salesInvoiceId,
      bill?.billId,
      requestedInvoices[index]?.salesInvoiceId,
      `preview-${index + 1}`,
    )
    const hasBillNumberField =
      bill && typeof bill === 'object' && Object.prototype.hasOwnProperty.call(bill, 'billNumber')
    const resolvedBillNumber = hasBillNumberField
      ? (bill?.billNumber ?? null)
      : firstDefined(bill?.billNumber, resolvedSalesInvoiceId)
    const normalizedStatus = normalizePreviewBillStatus(bill)
    const items = normalizePreviewItems(bill)
    const errorCode = normalizePreviewBillErrorCode(bill)
    const normalizedSummaryResult = normalizePreviewBillSummary(bill?.summary, items)
    const shouldFailClosedUnsupportedVat =
      normalizedStatus.status === 'success' && normalizedSummaryResult.hasUnsupportedTaxBucket
    const resolvedStatus = shouldFailClosedUnsupportedVat ? 'error' : normalizedStatus.status
    const resolvedErrorCode = shouldFailClosedUnsupportedVat ? 'SOURCE_ITEM_VAT_INVALID' : errorCode

    return {
      billId: resolvedSalesInvoiceId,
      salesInvoiceId: resolvedSalesInvoiceId,
      billNumber: resolvedBillNumber,
      invoiceType: normalizePreviewBillInvoiceType(bill?.invoiceType),
      status: resolvedStatus,
      success: resolvedStatus === 'success',
      error:
        resolvedStatus === 'error'
          ? resolvePreviewBillErrorMessage(resolvedErrorCode, bill?.error)
          : null,
      errorCode: resolvedErrorCode,
      reasonCode: resolvedErrorCode,
      items,
      summary: normalizedSummaryResult.summary,
    }
  })

  const previewSummary = normalizePreviewAggregateSummary(source.summary, normalizedBills)
  const successCount = normalizedBills.filter((bill) => bill.status === 'success').length
  const failureCount = normalizedBills.filter((bill) => bill.status === 'error').length

  return {
    summary: previewSummary,
    meta: {
      totalPreviews: toNumber(source.summary?.totalPreviews, normalizedBills.length),
      successCount,
      failureCount,
    },
    bills: normalizedBills,
  }
}

function normalizeDraftPreviewPayload(payload = {}, salesInvoiceId = null) {
  const source = extractResponseData(payload)
  const preview = source?.preview || source || {}
  const readiness = normalizeReadiness(firstDefined(source?.readiness, preview?.readiness, null))
  const readinessSummary = normalizeReadinessSummary(
    firstDefined(source?.readinessSummary, preview?.readinessSummary, null),
    readiness,
  )
  const readModel = normalizeCanonicalReadModel({
    source: preview,
    items: Array.isArray(source?.items) ? source.items : undefined,
    lineItems: Array.isArray(source?.lineItems) ? source.lineItems : undefined,
    convertedItems: Array.isArray(source?.convertedItems) ? source.convertedItems : undefined,
    summary: firstDefined(source?.summary, preview?.summary, null),
    fallbackSummary: firstDefined(source?.totals, preview?.totals, preview, {}),
    strictItemContract: true,
  })
  const normalizedItems = readModel.items
  const normalizedSummaryResult = normalizePreviewBillSummary(readModel.summary, normalizedItems)
  const normalizedSummary = normalizedSummaryResult.summary
  const hasUnsupportedTaxBucket = normalizedSummaryResult.hasUnsupportedTaxBucket
  const resolvedStatus = hasUnsupportedTaxBucket ? 'error' : 'success'
  const resolvedErrorCode = hasUnsupportedTaxBucket ? 'SOURCE_ITEM_VAT_INVALID' : null
  const resolvedSalesInvoiceId = firstDefined(source?.salesInvoiceId, salesInvoiceId)

  return {
    summary: normalizedSummary,
    meta: {
      totalPreviews: 1,
      successCount: resolvedStatus === 'success' ? 1 : 0,
      failureCount: resolvedStatus === 'error' ? 1 : 0,
    },
    bills: [
      {
        billId: resolvedSalesInvoiceId,
        salesInvoiceId: resolvedSalesInvoiceId,
        billNumber: firstDefined(source?.salesInvoiceId, salesInvoiceId),
        invoiceType: null,
        status: resolvedStatus,
        success: resolvedStatus === 'success',
        error:
          resolvedStatus === 'error'
            ? resolvePreviewBillErrorMessage(
                resolvedErrorCode,
                firstDefined(source?.error, preview?.error, null),
              )
            : null,
        errorCode: resolvedErrorCode,
        reasonCode: resolvedErrorCode,
        items: normalizedItems,
        summary: normalizedSummary,
      },
    ],
    seller: firstDefined(source?.seller, null),
    buyer: firstDefined(source?.buyer, null),
    items: normalizedItems,
    totals: normalizedSummary,
    readiness,
    readinessSummary,
    normalizedPayloadHash: firstDefined(
      source?.normalizedPayloadHash,
      preview?.normalizedPayloadHash,
      null,
    ),
    artifacts: normalizeArtifacts(
      firstDefined(source?.artifacts, preview?.artifacts, null),
      firstDefined(source?.artifact, preview?.artifact, null),
    ),
  }
}

function generateBulkIssueIdempotencyKey() {
  const timePart = Date.now().toString(36)
  const randomPart = Math.random().toString(36).slice(2, 16)
  return `bulk-issue-${timePart}-${randomPart}`.slice(0, 64)
}

function resolveBulkIssueIdempotency({
  normalizedSubmitPayload,
  previousFingerprint,
  previousKey,
} = {}) {
  const fingerprint = buildBulkIssueSubmitFingerprint(normalizedSubmitPayload)
  const explicitKey = normalizedSubmitPayload?.idempotencyKey || null

  if (explicitKey) {
    return {
      key: explicitKey,
      fingerprint,
    }
  }

  if (previousFingerprint === fingerprint && previousKey) {
    return {
      key: previousKey,
      fingerprint,
    }
  }

  return {
    key: generateBulkIssueIdempotencyKey(),
    fingerprint,
  }
}

function buildFallbackFacet(salesInvoiceId, saleType) {
  const normalizedSaleType = requireCanonicalSaleType(
    saleType,
    'Thiếu saleType hợp lệ để dựng fallback facet',
  )

  return normalizeRetailFacet(
    {
      salesInvoiceId,
      saleType: normalizedSaleType,
      workState: 'none',
      allowedActions: ['bootstrap-draft'],
      actionContext: {
        allowedActions: ['bootstrap-draft'],
        blockingReasons: [],
      },
    },
    normalizedSaleType,
  )
}

function isCanonicalBlockedDetailCode(code) {
  return [
    'CONTRACT_GONE',
    'EINVOICE_SNAPSHOT_MISSING',
    'EINVOICE_SNAPSHOT_REPAIR_REQUIRED',
    'BILL_MODE_UNSUPPORTED',
  ].includes(
    String(code || '')
      .trim()
      .toUpperCase(),
  )
}

function buildBlockedDetailState(state, context, normalizedError) {
  const blockerCode = String(normalizedError?.code || 'EINVOICE_DETAIL_BLOCKED')
    .trim()
    .toUpperCase()
  const blockerMessage =
    normalizedError?.message || 'Chi tiết HĐĐT đang bị block theo canonical contract mới.'
  const blockingReason = {
    action: '*',
    code: blockerCode,
    reasonCode: blockerCode,
    message: blockerMessage,
  }
  const readinessError = {
    code: blockerCode,
    field: null,
    message: blockerMessage,
  }
  const normalizedSaleType = normalizeSaleType(firstDefined(context?.saleType, null))

  return {
    salesInvoiceId: context?.salesInvoiceId,
    saleType: normalizedSaleType,
    laneKey: buildSalesInvoiceLaneKey(context?.salesInvoiceId, normalizedSaleType),
    billNumber: null,
    workState: null,
    status: normalizeStatus(null, EINVOICE_STATUS.DRAFT),
    taxStatus: normalizeTaxStatus(null, null),
    einvoice: null,
    allowedActions: [],
    blockingReasons: [blockingReason],
    actionContext: {
      allowedActions: [],
      blockingReasons: [blockingReason],
      cancelFormType: null,
      actionDescriptions: {},
    },
    fieldSemantics: {},
    readiness: {
      isReady: false,
      blockingErrors: [readinessError],
      warnings: [],
      missingRequiredFields: [],
    },
    readinessSummary: {
      isReady: false,
      blockingErrorCount: 1,
      warningCount: 0,
      missingRequiredCount: 0,
      providerStatus: null,
    },
    snapshot: null,
    summary: null,
    artifacts: {},
    normalizedPayloadHash: null,
    feedbackContent: blockerMessage,
    blockedContractCode: blockerCode,
    einvoiceBlockerType: normalizedError?.einvoiceBlockerType || null,
  }
}

function extractBatchFacetErrorCode(item = {}) {
  return firstDefined(item?.error?.code, item?.code, item?.errorCode, null)
}

function mapBatchFacetErrorMessage(errorCode, fallbackMessage) {
  if (errorCode === 'BILL_TYPE_MISMATCH') {
    return 'Hoá đơn không thuộc lane e-invoice đã chọn'
  }

  if (errorCode === 'BILL_NOT_FOUND') {
    return 'Không tìm thấy hoá đơn trong lane e-invoice đã chọn'
  }

  if (errorCode === 'BILL_MODE_UNSUPPORTED') {
    return 'Mode hóa đơn giao hàng chưa hỗ trợ'
  }

  return fallbackMessage || 'Không thể tải trạng thái HĐĐT cho hoá đơn này'
}

function buildFacetFromBatchError({ salesInvoiceId, saleType, errorCode, message }) {
  const normalizedSaleType = requireCanonicalSaleType(
    saleType,
    'Thiếu saleType hợp lệ để chuẩn hoá lỗi facet',
  )
  const normalizedMessage = mapBatchFacetErrorMessage(errorCode, message)

  return normalizeRetailFacet(
    {
      salesInvoiceId,
      saleType: normalizedSaleType,
      workState: 'failed',
      feedbackContent: normalizedMessage,
      actionContext: {
        allowedActions: [],
        blockingReasons: [
          {
            code: errorCode || null,
            reasonCode: errorCode || null,
            message: normalizedMessage,
          },
        ],
      },
    },
    normalizedSaleType,
  )
}

async function refetchCanonicalDetailAndFacet(dispatch, context = {}) {
  if (!context?.salesInvoiceId || !context?.saleType) {
    return null
  }

  const detailPayload = {
    salesInvoiceId: context.salesInvoiceId,
    saleType: context.saleType,
  }

  const [detailResult] = await Promise.all([
    dispatch(EINVOICE_FETCH_DETAIL, detailPayload),
    dispatch(EINVOICE_FETCH_FACET, {
      ...detailPayload,
      force: true,
    }),
  ])

  return detailResult
}

// ─── State ─────────────────────────────────────────────────
const state = {
  list: [],
  facetsBySalesInvoiceId: {},
  facetLoadingBySalesInvoiceId: {},
  totalItems: 0,
  totalPages: 0,
  detail: createDetailCacheState(),
  filter: defaultFilter(),
  isLoading: false,
  isActionLoading: false,
  isBatchActionLoading: false,
  error: null,
  vatPreview: {
    isLoading: false,
    data: null,
    error: null,
  },
  billInfo: {
    isLoading: false,
    data: null,
    error: null,
  },
  bulkIssue: {
    requestPayload: null,
    submitFingerprint: null,
    idempotencyKey: null,
    phase: 'idle',
    isPreChecking: false,
    isProcessing: false,
    jobId: null,
    preCheckResult: null,
    progress: null,
    result: null,
    error: null,
  },
  bulkDelete: {
    phase: 'idle',       // 'idle' | 'review' | 'zeroEligible' | 'processing' | 'result' | 'error'
    eligibleItems: [],
    blockedItems: [],
    saleType: null,
    progress: { processed: 0, total: 0, currentItem: null },
    result: { succeeded: [], failed: [] },
    error: null,
  },
}

// ─── Getters ───────────────────────────────────────────────
const getters = {
  einvoiceList: (s) => s.list,
  einvoiceFacetBySalesInvoiceId:
    (s) =>
    (salesInvoiceId, saleType = null) =>
      findCachedFacetEntry(s.facetsBySalesInvoiceId, salesInvoiceId, saleType).value || null,
  einvoiceFacetLoadingBySalesInvoiceId:
    (s) =>
    (salesInvoiceId, saleType = null) =>
      findFacetLoadingState(s.facetLoadingBySalesInvoiceId, salesInvoiceId, saleType),
  einvoiceTotalItems: (s) => s.totalItems,
  einvoiceTotalPages: (s) => s.totalPages,
  einvoiceDetail: (s) => getCurrentDetailEntity(s),
  einvoiceFilter: (s) => s.filter,
  einvoiceIsLoading: (s) => s.isLoading,
  einvoiceIsDetailLoading: (s) =>
    Boolean(s.detail.currentKey && s.detail.loadingByKey[s.detail.currentKey]),
  einvoiceIsActionLoading: (s) => s.isActionLoading,
  einvoiceIsBatchActionLoading: (s) => s.isBatchActionLoading,
  einvoiceError: (s) => s.error,
  einvoiceIsRetailOnly: (s) => {
    const billTypes = normalizeBillTypesOrDefault(s.filter.billTypes)
    return billTypes.every((billType) => isRetailLikeBillType(billType))
  },
  einvoiceIsRetail: (s, g) => g.einvoiceIsRetailOnly,
  einvoiceCanIssue: (s) => {
    const detail = getCurrentDetailEntity(s)
    if (!detail) return false
    return [
      EINVOICE_STATUS.DRAFT,
      EINVOICE_STATUS.READY_TO_ISSUE,
      EINVOICE_STATUS.ISSUE_FAILED, // Allow retry
    ].includes(detail.status)
  },
  einvoiceCanEdit: (s) => {
    const detail = getCurrentDetailEntity(s)
    if (!detail) return false
    return [
      EINVOICE_STATUS.DRAFT,
      EINVOICE_STATUS.READY_TO_ISSUE,
      EINVOICE_STATUS.ISSUE_FAILED,
    ].includes(detail.status)
  },
  einvoiceCanCorrect: (s) => {
    const detail = getCurrentDetailEntity(s)
    if (!detail) return false
    // Allow correction (Adjust/Replace) only when accepted by Tax Authority or Issued (if not yet sent to Tax)
    // Depending on business rule, usually Issued is enough to start correction if CQT is async.
    return [EINVOICE_STATUS.ISSUED, EINVOICE_STATUS.TAX_ACCEPTED].includes(detail.status)
  },
  einvoiceCanRefreshTax: (s) => {
    const detail = getCurrentDetailEntity(s)
    if (!detail) return false
    return [EINVOICE_STATUS.ISSUED, EINVOICE_STATUS.TAX_PENDING].includes(detail.status)
  },
  einvoiceCanDownloadPdf: (s) => {
    const detail = getCurrentDetailEntity(s)
    if (!detail) return false
    // Can download PDF if issued
    return detail.status >= EINVOICE_STATUS.ISSUED
  },
  vatPreviewIsLoading: (s) => s.vatPreview.isLoading,
  vatPreviewData: (s) => s.vatPreview.data,
  vatPreviewError: (s) => s.vatPreview.error,
  vatPreviewSummary: (s) => s.vatPreview.data?.summary || null,
  vatPreviewBills: (s) => s.vatPreview.data?.bills || [],
  billInfoIsLoading: (s) => s.billInfo.isLoading,
  billInfoData: (s) => s.billInfo.data,
  billInfoError: (s) => s.billInfo.error,
  billInfoBills: (s) => s.billInfo.data?.bills || [],
  einvoiceBulkIssue: (s) => s.bulkIssue,
  einvoiceBulkDelete: (s) => s.bulkDelete,
}

// ─── Mutations ─────────────────────────────────────────────
const mutations = {
  [SET_LIST](s, { data, total, totalPages }) {
    s.list = data
    s.totalItems = total
    s.totalPages = totalPages
  },
  [REMOVE_LIST_ITEM](s, salesInvoiceId) {
    s.list = s.list.filter((item) => item.salesInvoiceId !== salesInvoiceId)
    s.totalItems = Math.max(0, s.totalItems - 1)
  },
  [SET_FACET](s, { salesInvoiceId, facet }) {
    const cacheKey = buildSalesInvoiceLaneKey(salesInvoiceId, facet?.saleType)
    if (!cacheKey) return

    s.facetsBySalesInvoiceId = {
      ...s.facetsBySalesInvoiceId,
      [cacheKey]: facet,
    }
  },
  [SET_FACET_LOADING](s, { salesInvoiceId, saleType = null, value }) {
    const cacheKey = buildSalesInvoiceLaneKey(salesInvoiceId, saleType)
    if (!cacheKey) return

    s.facetLoadingBySalesInvoiceId = {
      ...s.facetLoadingBySalesInvoiceId,
      [cacheKey]: value,
    }
  },
  [SET_DETAIL_CURRENT_KEY](s, key) {
    s.detail.currentKey = key || null
  },
  [SET_DETAIL](s, payload) {
    const detail = payload?.detail || payload
    const cacheKey =
      payload?.key ||
      buildSalesInvoiceLaneKey(firstDefined(detail?.salesInvoiceId, null), detail?.saleType)

    if (!cacheKey) return

    s.detail.currentKey = cacheKey
    s.detail.entitiesByKey = {
      ...s.detail.entitiesByKey,
      [cacheKey]: detail,
    }
    s.detail.errorByKey = {
      ...s.detail.errorByKey,
      [cacheKey]: null,
    }
  },
  [SET_LOADING](s, val) {
    s.isLoading = val
  },
  [SET_DETAIL_LOADING](s, { key, value }) {
    if (!key) return

    s.detail.loadingByKey = {
      ...s.detail.loadingByKey,
      [key]: value,
    }
  },
  [SET_DETAIL_ERROR](s, { key, error }) {
    if (!key) return

    s.detail.errorByKey = {
      ...s.detail.errorByKey,
      [key]: error || null,
    }
  },
  [SET_ACTION_LOADING](s, val) {
    s.isActionLoading = val
  },
  [SET_ERROR](s, err) {
    s.error = err
  },
  [SET_FILTER](s, nextFilter) {
    s.filter = { ...nextFilter }
  },
  [SET_PAGINATION](s, { page, limit }) {
    s.filter.page = page
    s.filter.limit = limit
  },
  [RESET_FILTER](s) {
    s.filter = defaultFilter()
  },
  [SET_BATCH_LOADING](s, val) {
    s.isBatchActionLoading = val
  },
  [SET_VAT_PREVIEW_LOADING](s, val) {
    s.vatPreview.isLoading = val
  },
  [SET_VAT_PREVIEW_DATA](s, data) {
    s.vatPreview.data = data
    s.vatPreview.error = null
  },
  [SET_VAT_PREVIEW_ERROR](s, error) {
    s.vatPreview.error = error
    s.vatPreview.data = null
  },
  [CLEAR_VAT_PREVIEW](s) {
    s.vatPreview = {
      isLoading: false,
      data: null,
      error: null,
    }
  },
  [SET_BILL_INFO_LOADING](s, val) {
    s.billInfo.isLoading = val
  },
  [SET_BILL_INFO_DATA](s, data) {
    s.billInfo.data = data
    s.billInfo.error = null
  },
  [SET_BILL_INFO_ERROR](s, error) {
    s.billInfo.error = error
    s.billInfo.data = null
  },
  [CLEAR_BILL_INFO](s) {
    s.billInfo = {
      isLoading: false,
      data: null,
      error: null,
    }
  },
  [SET_BULK_ISSUE_PRE_CHECKING](s, val) {
    s.bulkIssue.isPreChecking = val
  },
  [SET_BULK_ISSUE_REQUEST_PAYLOAD](s, payload) {
    s.bulkIssue.requestPayload = payload
  },
  [SET_BULK_ISSUE_IDEMPOTENCY](s, payload) {
    s.bulkIssue.submitFingerprint = payload?.fingerprint || null
    s.bulkIssue.idempotencyKey = payload?.key || null
  },
  [SET_BULK_ISSUE_PHASE](s, phase) {
    s.bulkIssue.phase = phase
  },
  [SET_BULK_ISSUE_PRE_CHECK_RESULT](s, result) {
    s.bulkIssue.preCheckResult = result
    s.bulkIssue.error = null
  },
  [SET_BULK_ISSUE_PROCESSING](s, val) {
    s.bulkIssue.isProcessing = val
  },
  [SET_BULK_ISSUE_JOB_ID](s, jobId) {
    s.bulkIssue.jobId = jobId
  },
  [SET_BULK_ISSUE_PROGRESS](s, progress) {
    s.bulkIssue.progress = progress
  },
  [SET_BULK_ISSUE_RESULT](s, result) {
    s.bulkIssue.result = result
    if (result !== null && result !== undefined) {
      s.bulkIssue.isProcessing = false
    }
  },
  [SET_BULK_ISSUE_ERROR](s, error) {
    s.bulkIssue.error = error
  },
  [RESET_BULK_ISSUE](s) {
    s.bulkIssue = {
      requestPayload: null,
      submitFingerprint: null,
      idempotencyKey: null,
      phase: 'idle',
      isPreChecking: false,
      isProcessing: false,
      jobId: null,
      preCheckResult: null,
      progress: null,
      result: null,
      error: null,
    }
  },

  // ─── Bulk Delete mutations ────────────────────────────────
  [SET_BULK_DELETE_PHASE](s, phase) {
    s.bulkDelete.phase = phase
  },
  [SET_BULK_DELETE_ELIGIBLE](s, { eligible, blocked, saleType }) {
    s.bulkDelete.eligibleItems = Array.isArray(eligible) ? eligible : []
    s.bulkDelete.blockedItems = Array.isArray(blocked) ? blocked : []
    s.bulkDelete.saleType = saleType || null
  },
  [SET_BULK_DELETE_PROGRESS](s, progress) {
    s.bulkDelete.progress = progress
  },
  [SET_BULK_DELETE_RESULT](s, result) {
    s.bulkDelete.result = result
  },
  [SET_BULK_DELETE_ERROR](s, error) {
    s.bulkDelete.error = error
  },
  [RESET_BULK_DELETE](s) {
    s.bulkDelete = {
      phase: 'idle',
      eligibleItems: [],
      blockedItems: [],
      saleType: null,
      progress: { processed: 0, total: 0, currentItem: null },
      result: { succeeded: [], failed: [] },
      error: null,
    }
  },
}

// ─── Actions ───────────────────────────────────────────────
const actions = {
  async [EINVOICE_FETCH_FACET](
    { commit, state: s },
    { salesInvoiceId, saleType, force = false } = {},
  ) {
    if (!salesInvoiceId) return null

    const normalizedSaleType = requireCanonicalSaleType(
      saleType,
      'Thiếu saleType hợp lệ để tải facet HĐĐT',
    )
    const cachedFacetEntry = findCachedFacetEntry(
      s.facetsBySalesInvoiceId,
      salesInvoiceId,
      normalizedSaleType,
    )
    if (!force && cachedFacetEntry.value) {
      return cachedFacetEntry.value
    }

    commit(SET_FACET_LOADING, {
      salesInvoiceId,
      saleType: normalizedSaleType,
      value: true,
    })
    try {
      const endpoint = buildCanonicalDetailEndpoint(salesInvoiceId, normalizedSaleType)
      const { data } = await ApiService.get(endpoint)
      const facet = normalizeRetailFacet(extractFirstResult(data), normalizedSaleType)
      const normalizedFacet = facet?.salesInvoiceId
        ? facet
        : buildFallbackFacet(salesInvoiceId, normalizedSaleType)

      commit(SET_FACET, { salesInvoiceId, facet: normalizedFacet })
      return normalizedFacet
    } catch (err) {
      if (err?.response?.status === 404) {
        const fallbackFacet = buildFallbackFacet(salesInvoiceId, normalizedSaleType)
        commit(SET_FACET, { salesInvoiceId, facet: fallbackFacet })
        return fallbackFacet
      }

      commit(
        SET_ERROR,
        err?.response?.data?.message || err?.message || 'Không thể tải trạng thái HĐĐT',
      )
      throw err
    } finally {
      commit(SET_FACET_LOADING, {
        salesInvoiceId,
        saleType: normalizedSaleType,
        value: false,
      })
    }
  },

  async [EINVOICE_FETCH_FACETS_BATCH]({ commit }, { salesInvoiceIds = [], saleType } = {}) {
    const normalizedIds = Array.from(
      new Set(
        salesInvoiceIds
          .map((salesInvoiceId) => String(salesInvoiceId || '').trim())
          .filter(Boolean),
      ),
    )

    if (!normalizedIds.length) return []

    const normalizedSaleType = requireCanonicalSaleType(
      saleType,
      'Thiếu saleType hợp lệ để tải facet batch HĐĐT',
    )

    normalizedIds.forEach((salesInvoiceId) => {
      commit(SET_FACET_LOADING, { salesInvoiceId, saleType: normalizedSaleType, value: true })
    })

    try {
      const { data } = await ApiService.post(buildBatchFacetEndpoint(normalizedSaleType), {
        salesInvoiceIds: normalizedIds,
      })
      const source = extractResponseData(data)
      const items = Array.isArray(source?.facets)
        ? source.facets
        : Array.isArray(source?.items)
          ? source.items
          : []
      const facetMap = new Map()

      items.forEach((item) => {
        const fallbackSalesInvoiceId = firstDefined(
          item?.salesInvoiceId,
          item?.billId,
          item?.salesInvoiceID,
          null,
        )
        const normalizedFacet = normalizeRetailFacet(item, normalizedSaleType)
        const resolvedSalesInvoiceId = String(
          firstDefined(normalizedFacet?.salesInvoiceId, fallbackSalesInvoiceId, ''),
        ).trim()

        if (!resolvedSalesInvoiceId) return

        const errorCode = extractBatchFacetErrorCode(item)
        const facet = errorCode
          ? buildFacetFromBatchError({
              salesInvoiceId: resolvedSalesInvoiceId,
              saleType: normalizedSaleType,
              errorCode,
              message: firstDefined(item?.error?.message, item?.message, null),
            })
          : normalizedFacet

        if (!facet?.salesInvoiceId) return

        facetMap.set(resolvedSalesInvoiceId, facet)
        commit(SET_FACET, {
          salesInvoiceId: resolvedSalesInvoiceId,
          facet,
        })
      })

      return normalizedIds
        .map((salesInvoiceId) => facetMap.get(salesInvoiceId) || null)
        .filter(Boolean)
    } catch (err) {
      commit(
        SET_ERROR,
        err?.response?.data?.message || err?.message || 'Không thể tải trạng thái HĐĐT hàng loạt',
      )
      throw err
    } finally {
      normalizedIds.forEach((salesInvoiceId) => {
        commit(SET_FACET_LOADING, {
          salesInvoiceId,
          saleType: normalizedSaleType,
          value: false,
        })
      })
    }
  },

  async [EINVOICE_FETCH_LIST]({ commit, state: s }) {
    commit(SET_LOADING, true)
    commit(SET_ERROR, null)
    try {
      const billTypes = requireBillTypes(s.filter.billTypes)
      const storeIds = Array.isArray(s.filter.storeIds) && s.filter.storeIds.length
        ? s.filter.storeIds
        : undefined
      const params = {
        billTypes,
        status: s.filter.status || undefined,
        keyword: s.filter.keyword || undefined,
        buyerTaxCode: s.filter.buyerTaxCode || undefined,
        codeShip: s.filter.codeShip || undefined,
        invoiceDateFrom: s.filter.dateFrom || undefined,
        invoiceDateTo: s.filter.dateTo || undefined,
        issueDateFrom: s.filter.issueDateFrom || undefined,
        issueDateTo: s.filter.issueDateTo || undefined,
        storeIds,
        page: s.filter.page,
        limit: s.filter.limit,
      }

      // Use dashboard endpoint #17
      const { data } = await ApiService.get(`${BASE_URL}`, {
        params,
        paramsSerializer: serializeEInvoiceListParams,
      })

      const normalized = normalizeInvoiceListResponse(data)
      commit(SET_LIST, {
        data: normalized.items,
        total: normalized.total,
        totalPages: normalized.totalPages,
      })
    } catch (err) {
      if (!err?.isClientValidationError) {
        console.error(err)
      }
      const normalizedError = createEInvoiceActionError(err, 'Không thể tải danh sách hoá đơn')
      commit(SET_ERROR, normalizedError.message)
    } finally {
      commit(SET_LOADING, false)
    }
  },

  async [EINVOICE_FETCH_DETAIL]({ commit, state: s }, payload) {
    let context = null
    try {
      context = resolveDetailRequestContext(s, payload)
      commit(SET_DETAIL_CURRENT_KEY, context.laneKey)
      commit(SET_DETAIL_LOADING, { key: context.laneKey, value: true })
      commit(SET_DETAIL_ERROR, { key: context.laneKey, error: null })
      commit(SET_ERROR, null)

      if (!context.salesInvoiceId) {
        throw new Error('Thiếu salesInvoiceId để tải chi tiết hoá đơn')
      }

      const endpoint = buildCanonicalDetailEndpoint(context.salesInvoiceId, context.saleType)
      const response = await ApiService.get(endpoint)
      const normalizedDetail = normalizeInvoiceDetail(response.data?.data || {})

      commit(SET_DETAIL, {
        key: context.laneKey,
        detail: normalizedDetail,
      })
      return normalizedDetail
    } catch (err) {
      const normalizedError = createEInvoiceActionError(err, 'Không thể tải chi tiết hoá đơn')

      if (err?.response?.status === 422 && normalizedError?.code === 'BILL_TYPE_MISMATCH') {
        normalizedError.message = 'Hoá đơn không thuộc lane e-invoice đã chọn'
      }

      commit(SET_ERROR, normalizedError.message)
      if (context && isCanonicalBlockedDetailCode(normalizedError?.code)) {
        const blockedDetail = buildBlockedDetailState(s, context, normalizedError)
        commit(SET_DETAIL, {
          key: context.laneKey,
          detail: blockedDetail,
        })
        commit(SET_DETAIL_ERROR, { key: context.laneKey, error: normalizedError.message })
        return blockedDetail
      }
      if (context?.laneKey) {
        commit(SET_DETAIL_ERROR, { key: context.laneKey, error: normalizedError.message })
      }
    } finally {
      if (context?.laneKey) {
        commit(SET_DETAIL_LOADING, { key: context.laneKey, value: false })
      }
    }
  },

  // Single draft creation (mapped to batch of 1)
  async [EINVOICE_CREATE_DRAFT]({ dispatch }, payload) {
    const salesInvoiceId = firstDefined(payload?.salesInvoiceId, payload?.billId, null)
    const canonicalBuyerOverride = buildCanonicalDraftOverride(payload?.override, {
      path: 'override',
    })
    const result = await dispatch(EINVOICE_CREATE_DRAFT_BATCH, {
      billIds: salesInvoiceId ? [salesInvoiceId] : [],
      overrides: salesInvoiceId ? { [salesInvoiceId]: canonicalBuyerOverride } : undefined,
    })
    const firstDetail = Array.isArray(result?.details) ? result.details[0] : null

    if (!firstDetail) {
      return result
    }

    return {
      id: firstDefined(firstDetail.id, firstDetail.draftId, null),
      salesInvoiceId: firstDefined(firstDetail.billId, salesInvoiceId, null),
      buyerName: firstDefined(firstDetail.buyerName, canonicalBuyerOverride.customerName, null),
      status: firstDetail.status,
    }
  },

  async [EINVOICE_UPDATE_DRAFT]({ commit, dispatch }, { salesInvoiceId, saleType, payload }) {
    commit(SET_ACTION_LOADING, true)
    try {
      const context = requireCanonicalActionContext(
        { salesInvoiceId, saleType },
        'cập nhật nháp hoá đơn',
      )
      const canonicalPayload = buildCanonicalDraftOverride(payload, {
        path: 'payload',
      })
      await ApiService.put(
        `${buildDraftPrefix(context.salesInvoiceId, context.saleType)}`,
        canonicalPayload,
      )

      return await refetchCanonicalDetailAndFacet(dispatch, context)
    } catch (err) {
      const normalizedError = createEInvoiceActionError(err, 'Không thể cập nhật hoá đơn')
      commit(SET_ERROR, normalizedError.message)
      throw normalizedError
    } finally {
      commit(SET_ACTION_LOADING, false)
    }
  },

  async [EINVOICE_VALIDATE_DRAFT]({ commit }, { salesInvoiceId, saleType, payload }) {
    commit(SET_ACTION_LOADING, true)
    try {
      const context = requireCanonicalActionContext(
        { salesInvoiceId, saleType },
        'kiểm tra nháp hoá đơn',
      )
      const canonicalPayload = buildCanonicalDraftOverride(payload, {
        path: 'payload',
      })
      const { data } = await ApiService.post(
        `${buildDraftPrefix(context.salesInvoiceId, context.saleType)}/validate`,
        canonicalPayload,
      )
      const response = extractResponseData(data)
      return {
        ...response,
        isValid: Boolean(response?.isValid),
        errors: Array.isArray(response?.errors) ? response.errors : [],
        readiness: normalizeReadiness(response?.readiness),
        readinessSummary: normalizeReadinessSummary(
          response?.readinessSummary,
          response?.readiness,
        ),
        normalizedPayloadHash: firstDefined(response?.normalizedPayloadHash, null),
      }
    } catch (err) {
      const normalizedError = createEInvoiceActionError(err, 'Không thể kiểm tra nháp hoá đơn')
      commit(SET_ERROR, normalizedError.message)
      throw normalizedError
    } finally {
      commit(SET_ACTION_LOADING, false)
    }
  },

  async [EINVOICE_ISSUE]({ commit, dispatch }, payload = {}) {
    commit(SET_ACTION_LOADING, true)
    let context = null
    try {
      context = requireCanonicalActionContext(payload, 'phát hành hoá đơn')
      const result = await issueEInvoiceAction(context)
      await refetchCanonicalDetailAndFacet(dispatch, context)
      return result
    } catch (err) {
      try {
        if (context?.laneKey) {
          await refetchCanonicalDetailAndFacet(dispatch, context)
        }
      } catch (_) {
        // Keep original error as source of truth for the caller.
      }
      const normalizedError = createEInvoiceActionError(err, 'Phát hành thất bại')
      commit(SET_ERROR, normalizedError.message)
      throw normalizedError
    } finally {
      commit(SET_ACTION_LOADING, false)
    }
  },

  async [EINVOICE_CANCEL]({ commit, dispatch }, payload = {}) {
    commit(SET_ACTION_LOADING, true)
    try {
      const context = requireCanonicalActionContext(payload, 'huỷ hoá đơn')
      const requestPayload = buildCancelPayload(payload)
      const result = await cancelEInvoiceAction(context, requestPayload)
      await refetchCanonicalDetailAndFacet(dispatch, context)
      return result
    } catch (err) {
      const normalizedError = createEInvoiceActionError(err, 'Huỷ hoá đơn thất bại')
      commit(SET_ERROR, normalizedError.message)
      throw normalizedError
    } finally {
      commit(SET_ACTION_LOADING, false)
    }
  },

  async [EINVOICE_DELETE_DRAFT]({ commit }, payload = {}) {
    commit(SET_ACTION_LOADING, true)
    try {
      const context = requireCanonicalActionContext(payload, 'xoá bản nháp hoá đơn')
      const { data } = await ApiService.delete(
        buildDeleteDraftEndpoint(context.salesInvoiceId, context.saleType),
      )
      const result = adaptDeleteDraftResponse(extractResponseData(data))
      commit(REMOVE_LIST_ITEM, context.salesInvoiceId)
      return result
    } catch (err) {
      const normalizedError = createDeleteDraftError(err)
      commit(SET_ERROR, normalizedError.message)
      throw normalizedError
    } finally {
      commit(SET_ACTION_LOADING, false)
    }
  },

  async [EINVOICE_ADJUST]({ commit, dispatch }, payload = {}) {
    commit(SET_ACTION_LOADING, true)
    try {
      const context = requireCanonicalActionContext(payload, 'điều chỉnh hoá đơn')
      const requestPayload = normalizeCanonicalAdjustPayload(payload)
      const result = await adjustEInvoiceAction(context, requestPayload)
      await refetchCanonicalDetailAndFacet(dispatch, context)
      return result
    } catch (err) {
      const normalizedError = createEInvoiceActionError(err, 'Điều chỉnh thất bại')
      commit(SET_ERROR, normalizedError.message)
      throw normalizedError
    } finally {
      commit(SET_ACTION_LOADING, false)
    }
  },

  async [EINVOICE_REPLACE]({ commit, dispatch }, payload = {}) {
    commit(SET_ACTION_LOADING, true)
    try {
      const context = requireCanonicalActionContext(payload, 'thay thế hoá đơn')
      const requestPayload = normalizeCanonicalReplacePayload(payload)
      const result = await replaceEInvoiceAction(context, requestPayload)
      await refetchCanonicalDetailAndFacet(dispatch, context)
      return result
    } catch (err) {
      const normalizedError = createEInvoiceActionError(err, 'Thay thế thất bại')
      commit(SET_ERROR, normalizedError.message)
      throw normalizedError
    } finally {
      commit(SET_ACTION_LOADING, false)
    }
  },

  async [EINVOICE_REFRESH_TAX]({ commit, dispatch }, payload = {}) {
    commit(SET_ACTION_LOADING, true)
    try {
      const context = requireCanonicalActionContext(payload, 'kiểm tra trạng thái CQT')
      const responseData = await refreshEInvoiceTaxStatusAction(context)
      await refetchCanonicalDetailAndFacet(dispatch, context)
      return responseData
    } catch (err) {
      const normalizedError = createEInvoiceActionError(err, 'Kiểm tra CQT thất bại')
      commit(SET_ERROR, normalizedError.message)
      throw normalizedError
    } finally {
      commit(SET_ACTION_LOADING, false)
    }
  },

  async [EINVOICE_FETCH_PDF]({ state: s }, payload) {
    try {
      const payloadObject = payload && typeof payload === 'object' ? payload : {}
      const invoiceId =
        typeof payload === 'object'
          ? firstDefined(payloadObject?.id, payloadObject?.einvoice?.id, null)
          : payload
      const payloadLaneKey = buildSalesInvoiceLaneKey(
        normalizeRouteKeyValue(firstDefined(payloadObject?.salesInvoiceId, null)),
        normalizeSaleType(firstDefined(payloadObject?.saleType, null)),
      )
      const currentDetail = getCurrentDetailEntity(s)
      const detail =
        getDetailEntityByKey(s, payloadLaneKey) ||
        (currentDetail &&
        invoiceId &&
        firstDefined(currentDetail?.id, currentDetail?.einvoice?.id, null) === invoiceId
          ? currentDetail
          : payloadObject)
      const pdfRequest = inferPdfEndpoint(invoiceId, detail)
      const requestBody = pdfRequest.method === 'post' ? {} : undefined

      console.log('[EINVOICE_FETCH_PDF] input payload:', payload)
      console.log('[EINVOICE_FETCH_PDF] resolved request:', {
        invoiceId,
        payloadLaneKey,
        detailStatus: detail?.status,
        detailArtifacts: detail?.artifacts,
        pdfRequest,
        requestBody,
      })

      const { data } =
        pdfRequest.method === 'post'
          ? await ApiService.post(pdfRequest.route, requestBody)
          : await ApiService.get(pdfRequest.route)

      console.log('[EINVOICE_FETCH_PDF] response:', {
        route: pdfRequest.route,
        method: pdfRequest.method,
        dataKeys: data && typeof data === 'object' ? Object.keys(data) : typeof data,
        pdfBase64Length:
          typeof data?.pdfBase64 === 'string'
            ? data.pdfBase64.length
            : typeof data?.data?.pdfBase64 === 'string'
              ? data.data.pdfBase64.length
              : null,
        pdfUrl: data?.pdfUrl || data?.url || data?.data?.pdfUrl || data?.data?.url || null,
        rawData: data,
      })
      return pdfRequest.method === 'post'
        ? normalizePreviewPdfPayload(data)
        : normalizeIssuedPdfPayload(data)
    } catch (err) {
      throw createEInvoiceActionError(err, 'Không thể tải PDF')
    }
  },

  async [EINVOICE_RESEND_EMAIL]({ commit, dispatch }, payload = {}) {
    commit(SET_ACTION_LOADING, true)
    try {
      const context = requireCanonicalActionContext(payload, 'gửi lại email hoá đơn')
      const requestPayload = buildResendEmailPayload(payload)
      const result = await resendEInvoiceEmailAction(context, requestPayload)
      await refetchCanonicalDetailAndFacet(dispatch, context)
      return result
    } catch (err) {
      const normalizedError = createEInvoiceActionError(err, 'Gửi email thất bại')
      commit(SET_ERROR, normalizedError.message)
      throw normalizedError
    } finally {
      commit(SET_ACTION_LOADING, false)
    }
  },

  [EINVOICE_SET_FILTER]({ commit, dispatch, state: s }, partial = {}) {
    let nextFilter = null
    try {
      nextFilter = normalizeAppliedFilter(
        {
          ...s.filter,
          ...partial,
        },
        s.filter,
      )
    } catch (err) {
      const normalizedError = createEInvoiceActionError(err, 'Bộ lọc hóa đơn điện tử không hợp lệ')
      commit(SET_ERROR, normalizedError.message)
      return Promise.reject(normalizedError)
    }

    if (hasFilterCriteriaChanged(s.filter, nextFilter)) {
      nextFilter.page = 1
    }

    commit(SET_FILTER, nextFilter)
    return dispatch(EINVOICE_FETCH_LIST)
  },

  [EINVOICE_RESET_FILTER]({ commit, dispatch, state: s }) {
    commit(SET_FILTER, resetSubordinateFilters(s.filter))
    return dispatch(EINVOICE_FETCH_LIST)
  },

  async [EINVOICE_CREATE_DRAFT_BATCH]({ commit }, payload = {}) {
    commit(SET_BATCH_LOADING, true)
    try {
      const requestPayload = normalizeDraftBatchPayload(payload)
      const { data } = await ApiService.post(`${BASE_URL}/drafts/batch`, requestPayload)
      return data.data
    } catch (err) {
      const normalizedError = createEInvoiceActionError(err, 'Tạo nháp hàng loạt thất bại')
      commit(SET_ERROR, normalizedError.message)
      throw normalizedError
    } finally {
      commit(SET_BATCH_LOADING, false)
    }
  },

  // No longer polling job, as endpoint #2 returns results directly.
  // Keeping for compatibility if needed, but it's empty now.
  async [EINVOICE_POLL_JOB](_, jobId) {
    console.warn('Poll Job deprecated in Real API')
    return null
  },

  /**
   * Lookup company info by tax code from Vietnam tax authority.
   * Endpoint: GET /einvoice/companies?taxCode=...
   *
   * Response format:
   * - Found: { data: { taxCode, name, shortName, address, status } }
   * - Not found: { data: null }
   *
   * @param {string} taxCode - Tax code to lookup (MST 10 or 10-3 digits)
   * @returns {Promise<{ found: boolean, companyName?: string, address?: string, taxCode?: string }>}
   */
  async [EINVOICE_LOOKUP_TAX_CODE](_, taxCode) {
    try {
      const normalizedTaxCode = (taxCode || '').trim()
      const { data } = await ApiService.get(`${BASE_URL}/companies`, {
        params: { taxCode: normalizedTaxCode },
      })

      const company = data.data

      // Not found: { data: null }
      if (!company) {
        return { found: false }
      }

      // Found: { data: { taxCode, name, address, ... } }
      return {
        found: true,
        companyName: company.name || '',
        shortName: company.shortName || '',
        address: company.address || '',
        taxCode: company.taxCode || normalizedTaxCode,
        status: company.status || '',
      }
    } catch (err) {
      // 404 means not found
      if (err?.response?.status === 404) {
        return { found: false }
      }
      throw err
    }
  },

  async [EINVOICE_PREVIEW_CALCULATION]({ commit }, payload = {}) {
    commit(SET_VAT_PREVIEW_LOADING, true)
    commit(SET_VAT_PREVIEW_ERROR, null)
    try {
      const requestPayload = normalizePreviewCalculationRequest(payload)
      const { data } = await ApiService.post(`${BASE_URL}/preview-calculation`, requestPayload)
      const normalizedPreview = normalizePreviewCalculationResponse(data, requestPayload.invoices)
      commit(SET_VAT_PREVIEW_DATA, normalizedPreview)
      return normalizedPreview
    } catch (err) {
      const normalizedError = createEInvoiceActionError(
        err,
        'Không thể tải preview. Vui lòng thử lại.',
      )
      const errorData = {
        message: normalizedError.message,
        code: normalizedError.code,
        isConfigMissing: normalizedError.isConfigMissing,
        reasonCode: normalizedError.reasonCode,
      }
      commit(SET_VAT_PREVIEW_ERROR, errorData)
      throw normalizedError
    } finally {
      commit(SET_VAT_PREVIEW_LOADING, false)
    }
  },

  [EINVOICE_CLEAR_PREVIEW]({ commit }) {
    commit(CLEAR_VAT_PREVIEW)
  },

  /**
   * Fetch e-invoice info for multiple bills.
   * Used to populate bulk buyer entry grid with existing customer data.
   *
   * @param {string[]} billIds - Array of bill IDs
   * @returns {Promise<{ bills: Array<{ billId, billCode, customerName, customerEmail, ... }> }>}
   */
  async [EINVOICE_FETCH_BILL_INFO]({ commit }, billIds) {
    commit(SET_BILL_INFO_LOADING, true)
    commit(SET_BILL_INFO_ERROR, null)
    try {
      const params = { billIds: billIds.join(',') }
      const { data } = await ApiService.get('v1/bills/einvoice-info', { params })

      // Normalize response to { bills: [...] }
      const normalized = {
        bills: data.data?.bills || data.data || [],
      }

      commit(SET_BILL_INFO_DATA, normalized)
      return normalized
    } catch (err) {
      const normalizedError = createEInvoiceActionError(
        err,
        'Không thể tải thông tin hóa đơn. Vui lòng thử lại.',
      )
      const errorData = {
        message: normalizedError.message,
        code: normalizedError.code,
      }
      commit(SET_BILL_INFO_ERROR, errorData)
      throw normalizedError
    } finally {
      commit(SET_BILL_INFO_LOADING, false)
    }
  },

  async [EINVOICE_BULK_ISSUE_PRECHECK]({ commit }, payload) {
    commit(SET_BULK_ISSUE_PRE_CHECKING, true)
    commit(SET_BULK_ISSUE_ERROR, null)
    commit(SET_BULK_ISSUE_IDEMPOTENCY, null)
    commit(SET_BULK_ISSUE_PHASE, 'preChecking')
    commit(SET_BULK_ISSUE_PRE_CHECK_RESULT, null)
    try {
      const requestPayload = normalizeBulkPrecheckRequest(payload)
      commit(SET_BULK_ISSUE_REQUEST_PAYLOAD, requestPayload)
      const { data } = await ApiService.post(`${BASE_URL}/bulk/pre-check`, requestPayload)
      const result = normalizeBulkPrecheckResponse(extractResponseData(data))
      commit(SET_BULK_ISSUE_PRE_CHECK_RESULT, result)
      commit(SET_BULK_ISSUE_PHASE, result.summary?.eligible > 0 ? 'review' : 'zeroEligible')
      return result
    } catch (err) {
      const normalizedError = createBulkIssuePrecheckError(err)
      commit(SET_BULK_ISSUE_ERROR, normalizedError.message)
      commit(SET_BULK_ISSUE_PHASE, 'error')
      throw normalizedError
    } finally {
      commit(SET_BULK_ISSUE_PRE_CHECKING, false)
    }
  },

  async [EINVOICE_BULK_ISSUE_SUBMIT]({ commit, state: s }, payload) {
    commit(SET_BULK_ISSUE_PROCESSING, true)
    commit(SET_BULK_ISSUE_ERROR, null)
    commit(SET_BULK_ISSUE_PHASE, 'submitting')
    commit(SET_BULK_ISSUE_JOB_ID, null)
    commit(SET_BULK_ISSUE_PROGRESS, null)
    commit(SET_BULK_ISSUE_RESULT, null)
    try {
      const normalizedSubmitPayload = normalizeBulkIssueSubmitRequest(payload)
      const { key, fingerprint } = resolveBulkIssueIdempotency({
        normalizedSubmitPayload,
        previousFingerprint: s.bulkIssue.submitFingerprint,
        previousKey: s.bulkIssue.idempotencyKey,
      })
      const requestPayload = {
        invoiceType: normalizedSubmitPayload.invoiceType,
        action: s.bulkIssue.requestPayload?.action || 'issue',
        salesInvoiceIds: normalizedSubmitPayload.salesInvoiceIds,
      }

      commit(SET_BULK_ISSUE_REQUEST_PAYLOAD, requestPayload)
      commit(SET_BULK_ISSUE_IDEMPOTENCY, { key, fingerprint })

      const { data } = await ApiService.post(
        `${BASE_URL}/bulk-issue`,
        {
          invoiceType: normalizedSubmitPayload.invoiceType,
          salesInvoiceIds: normalizedSubmitPayload.salesInvoiceIds,
        },
        {
          headers: {
            'Idempotency-Key': key,
          },
        },
      )
      const result = normalizeBulkIssueSubmitResponse(extractResponseData(data))
      const jobId = result.jobId
      commit(SET_BULK_ISSUE_JOB_ID, jobId)
      commit(SET_BULK_ISSUE_PHASE, 'processing')
      return { jobId, idempotencyKey: key }
    } catch (err) {
      const normalizedError = createBulkIssueSubmitError(err)
      commit(SET_BULK_ISSUE_ERROR, normalizedError.message)
      commit(SET_BULK_ISSUE_PHASE, 'error')
      commit(SET_BULK_ISSUE_PROCESSING, false)
      throw normalizedError
    }
  },

  async [EINVOICE_BULK_ISSUE_PROGRESS]({ commit }, jobId) {
    const normalizedJobId = normalizeStringValue(jobId)
    if (!normalizedJobId) {
      const invalidJobError = new Error('Thiếu jobId để theo dõi tiến độ phát hành')
      invalidJobError.isRetryable = false
      throw invalidJobError
    }

    try {
      const { data } = await ApiService.get(`${BASE_URL}/bulk-issue/progress/${normalizedJobId}`)
      const progress = normalizeBulkIssueProgressResponse(extractResponseData(data))
      const normalizedProgress = {
        ...progress,
        jobId: progress.jobId || normalizedJobId,
      }

      commit(SET_BULK_ISSUE_ERROR, null)
      commit(SET_BULK_ISSUE_PROGRESS, normalizedProgress)
      if (normalizedProgress.isTerminal) {
        commit(SET_BULK_ISSUE_RESULT, normalizedProgress.terminalResult)
        commit(SET_BULK_ISSUE_PHASE, 'result')
      } else {
        commit(SET_BULK_ISSUE_PHASE, 'processing')
      }
      return normalizedProgress
    } catch (err) {
      const normalizedError = createBulkIssueProgressError(err)
      if (normalizedError.httpStatus === 404) {
        commit(SET_BULK_ISSUE_ERROR, normalizedError.message)
        commit(SET_BULK_ISSUE_PHASE, 'error')
        commit(SET_BULK_ISSUE_PROCESSING, false)
      } else {
        commit(SET_BULK_ISSUE_ERROR, normalizedError.message)
        commit(SET_BULK_ISSUE_PHASE, 'processing')
      }

      throw normalizedError
    }
  },

  /**
   * EINVOICE_BULK_DELETE_DRAFT action
   *
   * Stores the pre-classified eligible/blocked items in the bulkDelete Vuex slice
   * and transitions the phase to 'review' (or 'zeroEligible' when nothing is eligible).
   *
   * Note: The sequential deletion loop is NOT here — it lives in the modal component
   * (bulkDeleteModal.logic.executeBulkDelete). This action only handles the
   * pre-processing phase (classification result commit + phase transition).
   *
   * @param {Object} payload
   * @param {Array} payload.eligible - Pre-classified eligible items
   * @param {Array} payload.blocked - Pre-classified blocked items
   * @param {string} payload.saleType - 'retail' | 'wholesale'
   */
  async [EINVOICE_BULK_DELETE_DRAFT]({ commit }, { eligible, blocked, saleType }) {
    const normalizedEligible = Array.isArray(eligible) ? eligible : []
    const normalizedBlocked = Array.isArray(blocked) ? blocked : []

    commit(SET_BULK_DELETE_ELIGIBLE, {
      eligible: normalizedEligible,
      blocked: normalizedBlocked,
      saleType: saleType || null,
    })

    const nextPhase = normalizedEligible.length > 0 ? 'review' : 'zeroEligible'
    commit(SET_BULK_DELETE_PHASE, nextPhase)
  },
}

export default {
  state,
  getters,
  mutations,
  actions,
}
